<template>
  <Field v-slot="{ errors, meta }" :name="name" :rules="rules">
    <b-form-group :class="fieldsetClass">
      <select v-model="valueRef" :class="['form-control', getValidationState(errors, meta)]">
        <option v-for="option in options" :key="String(option.value)" :value="option.value">
          {{ option.text }}
        </option>
      </select>
      <div v-if="errors.length" class="invalid-feedback">
        {{ errors.join() }}
      </div>
    </b-form-group>
  </Field>
</template>

<script lang="ts">
import type { SelectOptions, SelectValue } from '@/types/Components/SelectOptions'
import { BFormGroup } from 'bootstrap-vue-next'
import { Field, useField } from 'vee-validate'
import { computed, watch, defineComponent, ref, type Ref } from 'vue'

export default defineComponent({
  name: 'VSelect',
  components: {
    BFormGroup,
    Field,
  },
  props: {
    name: {
      type: String,
      default: null,
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    value: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    options: {
      type: Array as () => SelectOptions[],
      default: () => [],
    },
    fieldsetClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { value: fieldValue } = useField(props.name)

    const innerValue = computed<SelectValue>({
      get: () => (props.value ?? null) as SelectValue,
      set: () => {},
    })
    const valueRef: Ref<SelectValue> = ref(null)

    watch(
      innerValue,
      (val) => {
        valueRef.value = val
      },
      { immediate: true },
    )

    watch(
      valueRef,
      (val) => {
        fieldValue.value = val
        emit('update:modelValue', val)
      },
      { immediate: true },
    )

    const getValidationState = (errors: any[], meta: any) => {
      if (!meta.touched && !meta.dirty) {
        return null
      }

      return errors.length ? 'is-invalid' : 'is-valid'
    }

    return {
      valueRef,
      innerValue,
      getValidationState,
    }
  },
})
</script>

<style lang="scss" scoped>
:deep(.form-group) {
  margin-bottom: 1rem;
}
</style>
